/**
 * W-060 — Notification composer. Stories: US-A08.
 *
 * AC1 bilingual title and body
 * AC2 audience: all users, or specific streams
 * AC3 destination: a subject, a lesson, or nothing
 * AC4 send test — only the composer's own admin account receives it  → WD-8
 * AC5 delivery count after sending
 */
import { useGetIdentity, useNotification } from '@refinedev/core'
import { Alert, Button, Card, Form, Input, Radio, Select, Space, Statistic, Typography } from 'antd'
import { useState } from 'react'
import { BilingualField } from '@/components'
import { SendTestConfirmDialog } from '@/components/dialogs'
import { recordAudit } from '@/lib/audit'
import { invokeEdgeFunction } from '@/lib/supabase'
import type { AdminIdentity } from '@/providers/auth-provider'
import { useSubjectOptions } from '../content-items/useSubjectOptions'
import { useStreams } from '../subjects/useStreams'
import { useAdminTranslate } from '@/providers/i18n-provider'

type Audience = 'all' | 'streams'
type DeepLinkKind = 'none' | 'subject' | 'content'

interface ComposerValues {
  title_ar: string
  title_fr: string
  body_ar: string
  body_fr: string
  audience: Audience
  stream_ids?: string[]
  deep_link_kind: DeepLinkKind
  deep_link_target?: string
}

/** Mirrors the mobile routes of docs/05 §4 so a tap lands on the right screen. */
function buildDeepLink(kind: DeepLinkKind, target?: string): string | null {
  if (kind === 'none' || !target) return null
  return kind === 'subject' ? `/subject/${target}` : `/lesson/${target}`
}

export const NotificationComposePage = () => {
  const translate = useAdminTranslate()
  const [form] = Form.useForm<ComposerValues>()
  const { streams } = useStreams()
  const { subjects } = useSubjectOptions()
  const { data: identity } = useGetIdentity<AdminIdentity>()
  const { open: notify } = useNotification()

  const [testOpen, setTestOpen] = useState(false)
  const [sending, setSending] = useState(false)
  const [delivered, setDelivered] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  const audience = (Form.useWatch('audience', form) ?? 'all') as Audience
  const deepLinkKind = (Form.useWatch('deep_link_kind', form) ?? 'none') as DeepLinkKind

  // T-005 AC4: a test send targets only the composer's own admin account, so
  // it uses the `users` audience kind rather than the form's `all`/`streams`
  // choice.
  const buildTarget = (values: ComposerValues, test: boolean, adminId?: string) => {
    if (test) return { kind: 'users' as const, user_ids: adminId ? [adminId] : [] }
    if (values.audience === 'streams') {
      return { kind: 'streams' as const, stream_ids: values.stream_ids ?? [] }
    }
    return { kind: 'all' as const }
  }

  const payload = (values: ComposerValues, test: boolean, adminId?: string) => ({
    title_ar: values.title_ar,
    title_fr: values.title_fr,
    body_ar: values.body_ar,
    body_fr: values.body_fr,
    deep_link: buildDeepLink(values.deep_link_kind, values.deep_link_target),
    target: buildTarget(values, test, adminId),
  })

  const send = async (test: boolean) => {
    const values = await form.validateFields()
    if (test && !identity?.id) {
      setError(translate('admin.screens.compose.sendFailed'))
      return
    }
    setSending(true)
    setError(null)
    try {
      // `notifications-send` (plural, T-005) dispatches push via Expo — a
      // service-role concern (docs/03 §4.4) — and returns the delivery count
      // for AC5.
      const result = await invokeEdgeFunction<{ delivered: number; notification_id: string }>(
        'notifications-send',
        payload(values, test, identity?.id),
      )
      setDelivered(result.delivered)
      if (!test) {
        await recordAudit({
          action: 'notification.send',
          target: 'notifications',
          targetId: result.notification_id,
          after: payload(values, false, identity?.id),
        })
        form.resetFields()
      }
      notify?.({
        type: 'success',
        message: test
          ? translate('admin.screens.compose.testSent')
          : translate('admin.screens.compose.sent'),
        description: translate('admin.screens.compose.deliveredCount', {
          count: result.delivered,
        }),
      })
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : translate('admin.screens.compose.sendFailed'),
      )
    } finally {
      setSending(false)
      setTestOpen(false)
    }
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ marginBottom: 0 }}>
        {translate('admin.pages.composeNotification')}
      </Typography.Title>

      {error ? <Alert type="error" showIcon message={error} /> : null}

      <Form<ComposerValues> form={form} layout="vertical">
        <Card
          size="small"
          title={translate('admin.screens.compose.messageSection')}
          style={{ marginBottom: 16 }}
        >
          <BilingualField
            name="title"
            label={translate('admin.columns.title')}
            requiredAr
            maxLength={80}
          />
          <BilingualField
            name="body"
            label={translate('admin.screens.compose.bodyLabel')}
            multiline
            requiredAr
            maxLength={240}
          />
          <Typography.Text type="secondary">
            {translate('admin.screens.compose.bilingualNotice')}
          </Typography.Text>
        </Card>

        <Card
          size="small"
          title={translate('admin.screens.compose.audienceSection')}
          style={{ marginBottom: 16 }}
        >
          <Form.Item name="audience" initialValue={'all' satisfies Audience}>
            <Radio.Group
              optionType="button"
              options={[
                { value: 'all', label: translate('admin.screens.compose.audienceAll') },
                { value: 'streams', label: translate('admin.screens.compose.audienceStreams') },
              ]}
            />
          </Form.Item>
          {audience === 'streams' ? (
            <Form.Item
              name="stream_ids"
              label={translate('admin.columns.streams')}
              rules={[
                { required: true, message: translate('admin.screens.compose.streamsRequired') },
              ]}
            >
              <Select
                mode="multiple"
                optionFilterProp="label"
                options={streams.map(stream => ({
                  value: stream.id,
                  label: `${stream.name_fr} — ${stream.name_ar}`,
                }))}
              />
            </Form.Item>
          ) : null}
        </Card>

        <Card
          size="small"
          title={translate('admin.screens.compose.destinationSection')}
          style={{ marginBottom: 16 }}
        >
          <Form.Item name="deep_link_kind" initialValue={'none' satisfies DeepLinkKind}>
            <Radio.Group
              optionType="button"
              options={[
                { value: 'none', label: translate('admin.screens.compose.destinationNone') },
                { value: 'subject', label: translate('admin.screens.compose.destinationSubject') },
                { value: 'content', label: translate('admin.screens.compose.destinationContent') },
              ]}
            />
          </Form.Item>
          {deepLinkKind === 'subject' ? (
            <Form.Item
              name="deep_link_target"
              label={translate('admin.columns.subject')}
              rules={[{ required: true }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={subjects.map(subject => ({
                  value: subject.id,
                  label: subject.name_fr,
                }))}
              />
            </Form.Item>
          ) : null}
          {deepLinkKind === 'content' ? (
            <Form.Item
              name="deep_link_target"
              label={translate('admin.screens.compose.lessonIdLabel')}
              rules={[{ required: true }]}
              help={translate('admin.screens.compose.lessonIdHelp')}
            >
              <Input />
            </Form.Item>
          ) : null}
        </Card>

        <Space>
          <Button onClick={() => setTestOpen(true)} loading={sending}>
            {translate('admin.screens.compose.sendTest')}
          </Button>
          <Button type="primary" onClick={() => void send(false)} loading={sending}>
            {translate('admin.screens.compose.send')}
          </Button>
        </Space>
      </Form>

      {delivered !== null ? (
        <Card size="small">
          <Statistic title={translate('admin.screens.compose.devicesReached')} value={delivered} />
        </Card>
      ) : null}

      {/* WD-8 */}
      <SendTestConfirmDialog
        open={testOpen}
        adminEmail={identity?.email ?? translate('admin.screens.compose.yourAccount')}
        onCancel={() => setTestOpen(false)}
        onConfirm={() => send(true)}
      />
    </Space>
  )
}
